import type React from "react";

import { useTranslation } from "react-i18next";

import { useBroadcastStats } from "../../hooks/useBroadcastStats";

import { encoderLabel, formatMbps, isOutputDegraded } from "./BroadcastHealth.utils";

/**
 * Salud del broadcast en el title bar, en una línea: fps, Mbps y frames caídos del encoder.
 * Se pinta como warning si alguna salida RTMP está degradada (cola creciendo / sin ack) y
 * atenuado si el OSR dejó de reportar. No renderiza nada hasta la primera foto con encoder.
 */
export const BroadcastHealth: React.FC = () => {
  const { t } = useTranslation("dashboard");

  const { stats, stale } = useBroadcastStats();

  if (!stats?.encoder) return null;

  const { encoder, outputs } = stats;

  const degraded = isOutputDegraded(outputs);

  const tooltip = [
    t("DASHBOARD.broadcastHealth.encoder", { encoder: encoderLabel(encoder.kind) }),
    degraded ? t("DASHBOARD.broadcastHealth.degraded") : null,
    stale ? t("DASHBOARD.broadcastHealth.stale") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const className = [
    "ml-3 inline-flex items-center gap-2 rounded-lg border px-2.5 py-0.5",
    "text-[11px] font-medium tabular-nums whitespace-nowrap",
    degraded
      ? "border-amber-500/40 bg-amber-500/15 text-amber-400"
      : "border-white/10 bg-white/5 text-foreground/70",
    stale ? "opacity-50" : "",
  ].join(" ");

  return (
    <div
      className={className}
      title={tooltip}
      role="status"
      data-degraded={degraded ? "true" : undefined}
      data-stale={stale ? "true" : undefined}
    >
      <span>{Math.round(encoder.fps)} fps</span>
      <span>{formatMbps(encoder.bitrateKbps)} Mbps</span>
      <span>
        {t("DASHBOARD.broadcastHealth.dropped", { n: encoder.droppedFrames })}
      </span>
    </div>
  );
};

export default BroadcastHealth;
